import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../utils/roles';
import './ExaminerProfilePage.css';

const ExaminerProfilePage = () => {
  const { user, updateUser } = useAuth(); // Hent updateUser fra context
  const fileInputRef = useRef(null); // Reference til den skjulte file input
  
  // State til formularer
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    profilePicture: ''
  });

  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState(null);

  // Udfyld formular med data når bruger loades
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        profilePicture: user.profilePicture || ''
      });
    }
  }, [user]);

  // Håndter billede upload til profilen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Tjek størrelse (maks 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Billedet må maksimalt fylde 2MB.' });
        return;
      }

      // Konverter fil til Base64 streng
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profilePicture: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Håndter opdatering af profil
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // apiClient sørger automatisk for at sende: Authorization: Bearer <TOKEN>
      const response = await apiClient.put(`/api/users/${user.id}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        profilePicture: formData.profilePicture
      });
      
      // Opdater den globale bruger context med de nye data
      updateUser(response.data);
      
      setMessage({ type: 'success', text: 'Profil opdateret!' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Kunne ikke opdatere oplysninger.' });
    }
  };

  // Håndter skift af kodeord
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Adgangskoderne er ikke ens.' });
      return;
    }

    try {
      await apiClient.put(`/api/users/${user.id}/password`, {
        newPassword: passwordData.newPassword
      });
      setMessage({ type: 'success', text: 'Adgangskode ændret!' });
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Fejl ved skift af adgangskode.' });
    }
  };

  // Helper til initialer i avatar
  const getInitials = () => {
    const f = formData.firstName ? formData.firstName[0] : '';
    const l = formData.lastName ? formData.lastName[0] : '';
    return (f + l).toUpperCase() || 'SC';
  };

  return (
    <div>
      
      {message && (
        <div style={{
          padding: '10px', marginBottom: '20px', borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24'
        }}>
          {message.text}
        </div>
      )}

      <div className="profile-container">
        
        {/* VENSTRE: Profilbillede */}
        <div className="profile-card-left">
          <div 
            className="avatar-wrapper"
            onClick={() => fileInputRef.current.click()} // Klik på billede åbner filvælger
            title="Klik for at skifte billede"
          >
            {formData.profilePicture ? (
              <img src={formData.profilePicture} alt="Profil" className="avatar-image" />
            ) : (
              <div className="avatar-placeholder">{getInitials()}</div>
            )}
            <div className="avatar-overlay">Skift</div>
          </div>
          
          {/* Skjult input felt */}
          <input 
            type="file" 
            ref={fileInputRef} 
            style={{display: 'none'}} 
            accept="image/*"
            onChange={handleImageChange}
          />

          <h2 className="profile-name">{formData.firstName} {formData.lastName}</h2>
          <span className="profile-role">{user?.role}</span>
          
          <div style={{textAlign: 'left', marginTop: '1rem', fontSize: '0.9rem', color: '#666'}}>
            <p style={{marginBottom: '5px'}}><strong>ID:</strong> {user?.id}</p>
            {user?.scId && <p><strong>Sprogcenter ID:</strong> {user?.scId}</p>}
            <p><strong>Status:</strong> <span style={{color: 'green'}}>Aktiv</span></p>
          </div>
        </div>

        {/* HØJRE: Formular */}
        <div className="profile-card-right">
          <h3 className="section-title">Generelle Oplysninger</h3>
          <form onSubmit={handleProfileUpdate}>
            <div className="form-grid">
              <div className="form-group">
                <label>Fornavn</label>
                <input 
                  type="text" 
                  value={formData.firstName} 
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Efternavn</label>
                <input 
                  type="text" 
                  value={formData.lastName} 
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                />
              </div>
              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <label>Email Adresse</label>
                <input 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            <button type="submit" className="save-btn">Gem Ændringer</button>
          </form>

          {/* Sektion 2: Sikkerhed */}
          <h3 className="section-title" style={{marginTop: '2rem'}}>Sikkerhed</h3>
          <form onSubmit={handlePasswordChange}>
            <div className="form-grid">
              <div className="form-group">
                <label>Ny Adgangskode</label>
                <input 
                  type="password" 
                  placeholder="Min. 6 tegn"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Bekræft Adgangskode</label>
                <input 
                  type="password" 
                  placeholder="Gentag adgangskode"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                />
              </div>
            </div>
            <button type="submit" className="save-btn" style={{backgroundColor: '#4a5568'}}>Skift Adgangskode</button>
          </form>

          {/* Sektion 3: Censor Opgaver (Vises KUN for TEACHER/CENSOR) */}
          {user?.role === ROLES.TEACHER && (
            <div style={{marginTop: '3rem'}}>
              <h3 className="section-title">Mine Opgaver (Termin Dec 2025)</h3>
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '1rem'}}>
                Herunder ses de hold, du er tildelt som censor for indeværende termin.
              </p>
              
              <table className="assignments-table">
                <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Sprogcenter</th>
                    <th>Prøve</th>
                    <th>Hold ID</th>
                    <th>Kontaktperson</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Mock data indtil vi har Del 4 på plads */}
                  <tr>
                    <td>15. Dec 2025</td>
                    <td>Sprogcenter Midt</td>
                    <td>PD3 - Mundtlig</td>
                    <td>HOLD-2025-88</td>
                    <td>Jens Hansen (tlf. 12345678)</td>
                  </tr>
                  <tr>
                    <td>16. Dec 2025</td>
                    <td>Sprogcenter Midt</td>
                    <td>PD3 - Mundtlig</td>
                    <td>HOLD-2025-89</td>
                    <td>Jens Hansen (tlf. 12345678)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ExaminerProfilePage;